import styled from "styled-components";
import { motion, AnimatePresence } from "motion/react";
import classNames from "classnames";
import { useRef, useEffect, useState } from "react";

import SectionTitle from "./SectionTitle";

export default function NavSection({
  tabKey,
  openTab,
  setOpenTab,
  children,
  title,
  icon,
  color,
  maxOpenHeight,
  hasSettings = false,
}) {
  const open = openTab === tabKey;

  const toggleTabOpen = () => setOpenTab(open ? false : tabKey);

  const wrapperRef = useRef();
  const [shineCenterX, setShineCenterX] = useState("50%");

  const mouseMove = ({ clientX }) => {
    setShineCenterX(`${((clientX - 8) / 324) * 100}%`);
  };

  return (
    <PaddingWrapper>
      <Padding open={open || !openTab} />
      <Wrapper
        style={{
          "--shineCenterX": shineCenterX,
          "--sectionColor": color,
        }}
        variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } }}
        className={classNames({ open })}
        ref={wrapperRef}
        onMouseMove={mouseMove}
      >
        <TopCowPrint />
        <BottomCowPrint />
        <SectionTitle
          title={title}
          icon={icon}
          color={color}
          open={open}
          onClick={toggleTabOpen}
          tabKey={tabKey}
          anyOtherTabIsOpen={!open && openTab !== false}
          hasSettings={hasSettings}
        />
        <AnimatePresence>
          {open && (
            <ChildrenContainer
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: "auto",
                transition: { type: "spring", bounce: 0.25 },
              }}
              exit={{
                opacity: 0,
                height: 0,
              }}
            >
              <ChildrenInside style={{ maxHeight: maxOpenHeight }}>
                {children}
              </ChildrenInside>
            </ChildrenContainer>
          )}
        </AnimatePresence>
      </Wrapper>
      <Padding open={open || !openTab} />
    </PaddingWrapper>
  );
}

const PaddingWrapper = styled.div`
  position: relative;
  z-index: 100;
  &:hover {
    z-index: 200;
  }
`;
const Wrapper = styled(motion.div)`
  flex-grow: 0;
  transition: background 0.4s;
  position: relative;

  &:before {
    content: "";
    position: absolute;
    background: var(--sectionColor);
    inset: 0;
    opacity: 0;
    transition: opacity 0.4s;
  }
  &.open:before {
    opacity: 1;
  }

  &:after {
    content: "";
    position: absolute;
    display: block;
    inset: 0;
    border-radius: 8px;
    overflow: hidden;
    background: radial-gradient(
      180px circle at var(--shineCenterX) 0%,
      rgba(255, 255, 255, 0.08),
      rgba(255, 255, 255, 0)
    );
    transition: opacity 0.2s;
    opacity: 0;
    display: none;
  }
  &.open:after {
    transition: opacity 2s;
  }
  &.open:hover:after {
    opacity: 1;
    transition: opacity 0.4s;
  }
`;
const ChildrenContainer = styled(motion.div)`
  overflow: hidden;
  position: relative;
`;
const ChildrenInside = styled.div`
  overflow: auto;
`;

function TopCowPrint() {
  return (
    <CowPrintWrapper>
      <svg
        id="Layer_1"
        data-name="Layer 1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 340 24"
      >
        <Path d="M0,13.41C28.14,8.57,63.86,0,111,0c83.16,0,73.14,15.71,122.66,15.89,38.35.14,72.48-15.6,106.34-6.42v14.54H0s0-10.59,0-10.59Z" />
      </svg>
    </CowPrintWrapper>
  );
}

const CowPrintWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 100%;
  z-index: 1000;
  width: 100%;
  height: 24px;
  transition:
    opacity 0.4s,
    visibility 0.4s,
    transform 0.4s;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;

  ${Wrapper}.open & {
    opacity: 1;
    visibility: visible;
  }
`;
const Path = styled.path`
  fill: var(--sectionColor);
`;

const BottomCowPrintWrapper = styled(CowPrintWrapper)`
  top: 100%;
  bottom: auto;
  transform: rotate(180deg);
  ${Wrapper}.open & {
    transform: rotate(180deg);
  }
`;

function BottomCowPrint() {
  return (
    <BottomCowPrintWrapper>
      <svg
        id="Layer_1"
        data-name="Layer 1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 340 24"
      >
        <Path d="M0,13.41C28.14,8.57,63.86,0,111,0c83.16,0,73.14,15.71,122.66,15.89,38.35.14,72.48-15.6,106.34-6.42v14.54H0s0-10.59,0-10.59Z" />
      </svg>
    </BottomCowPrintWrapper>
  );
}

const Padding = ({ open }) => {
  return <motion.div animate={{ height: open ? 18 : 0 }} />;
};
